import { widgetTargetLabel } from "../widgets/target-key-label.js";

/**
 * PB-4923 AC6 — a refused Proposal apply, as a sentence rather than a blob.
 *
 * The rail renders `PlayerAIToolProposalPayload.error`, and the host was
 * building it with `JSON.stringify(proposal.error)`. For most codes that was
 * merely unhelpful; for `WIDGET_EDITING_IN_PROGRESS` it is the whole point
 * missed, because that refusal carries WHICH field and WHO is holding it and
 * the user was shown `{"code":"WIDGET_EDITING_IN_PROGRESS","details":{…}}`.
 *
 * Data-blind, like everything else here: the caller supplies the name resolver
 * and, when it has them, the widget's params so the field can be named from its
 * own content. Both degrade independently.
 *
 * Two rules carried over from the human-save path
 * (`notify-widget-operations.ts`), so the two surfaces cannot disagree:
 *
 * - An unresolved actor is **"Someone else"**, never an id fragment. Naming the
 *   wrong person is worse than naming none.
 * - An unresolvable field falls back to impersonal wording rather than printing
 *   the raw target key, whose bracket grammar reads as a bug.
 *
 * Anything this does not recognise returns `null`, and the caller keeps its
 * existing behaviour — so no currently-rendered error changes.
 */

/** The shape the backend puts on a refusal; every field optional by design. */
export interface ProposalApplyErrorDetails {
  readonly reason?: unknown;
  readonly targetKey?: unknown;
  readonly actorId?: unknown;
}

export interface ProposalApplyErrorCopyOptions {
  /** Exact semantic tool id, supplied only for a settled apply refusal. */
  readonly toolId?: string;
  /** Resolves a user id to a display name, or `null` when it cannot. */
  readonly nameForActor?: (userId: string) => string | null;
  /** The widget's params, so a field can be named from its own content. */
  readonly params?: unknown;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * A readable sentence for a refused apply, or `null` when this does not
 * recognise the error and the caller should fall back.
 */
export function proposalApplyErrorCopy(
  error: unknown,
  options: ProposalApplyErrorCopyOptions = {},
): string | null {
  const record = asRecord(error);
  if (record === null) return null;
  const code = asString(record["code"]);
  if (code === null) return null;

  if (
    options.toolId === "hoshin.x_matrix.propose_changes" &&
    (code === "HOSHIN_XMATRIX_ITEM_CONFLICT" || code === "TARGET_STATE_CHANGED")
  ) {
    return "Nothing was applied. Read the latest X-Matrix and regenerate the proposal.";
  }

  const details: ProposalApplyErrorDetails = asRecord(record["details"]) ?? {};
  const targetKey = asString(details.targetKey);
  const actorId = asString(details.actorId);

  // `widgetTargetLabel` already returns a QUOTED label for an item ("“Machine
  // calibration”") and declines rather than print an id, so it is used as-is.
  const field =
    targetKey === null ? null : widgetTargetLabel(targetKey, options.params);

  const name =
    actorId === null ? null : asString(options.nameForActor?.(actorId));
  const who = name ?? "Someone else";

  switch (code) {
    case "WIDGET_EDITING_IN_PROGRESS":
      return field === null
        ? `${who} is editing this widget right now, so it wasn't changed.`
        : `${who} is editing ${field} right now, so it wasn't changed.`;
    case "TARGET_STATE_CHANGED":
      return field === null
        ? "This changed after the suggestion was prepared, so it wasn't applied."
        : `${field} changed after the suggestion was prepared, so it wasn't applied.`;
    case "RUN_STOPPED":
      // Task 14: Stop is not undo. An approved change Stop settled was either
      // never started or interrupted mid-apply, and only the canvas knows which,
      // so this claims neither outcome.
      return "Player AI was stopped before this change was confirmed. Check the canvas before trying again.";
    default:
      // Unknown code: say nothing rather than guess. The caller still shows
      // whatever it showed before.
      return null;
  }
}
